import { ReactElement, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { getElementAssemblyType, multiEditConfig } from '@editorUtils'
import { AxiosError } from 'axios'
import { every, find } from 'lodash-es'
import { v4 } from 'uuid'
import { Button, Stack, Typography } from '@mui/material'
import { WallSettings } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlignmentButton, HideButton } from '@ui/actions'
import { ErrorFieldBase } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { CollapsibleBox } from '@ui/structure'
import { useControlStore, useModelStore } from '@editorStores'
import {
  useElementTypes,
  useGuidToElementIncludingRips,
  useResultsInvalidation,
} from '@editorHooks'
import {
  AssemblyAssignmentForm,
  CollapsibleActionsBoxSection,
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleOpeningsBoxSection,
  CollapsibleWallRipsBoxSection,
  ElementAssembliesTable,
  FormCollapsibleSection,
  FormSectionIcons,
  MultiEditWallRipsStartEnd,
} from '@editorComponents'
import { useModelInvalidation, useStructuralPlanningSelection } from '@structuralPlanningHooks'
import {
  getModel,
  getTensileTransmissionGraph,
  getStiffeningIntervals,
  getStiffeningProposal,
  getVerticalTransmissionGraph,
} from '@queries'
import { createWall, deleteWall } from '@mutations'
import { buildErrorMessage } from 'src/constants'
import { alignSelectedWalls } from 'src/state/mutations/alignWalls'
import RipCopyForm, {
  COPY_RIPS_MULTI_TARGET_SELECTION_KEY,
} from '../PositionForm/components/RipCopyForm'
import WallAlignmentConfirmDialog from '../WallAlignmentConfirmDialog'
import CopyToOtherStoreys from './components/CopyToOtherStoreys'
import MergeWallForm from './components/MergeWallForm'
import MultiDelete from './components/MultiDelete'
import useCheckSettingsSection from './components/MultiEditCheckSettings/hooks/useCheckSettingsSection'
import useMultiEditCheckSettingElements from './components/MultiEditCheckSettings/hooks/useMultiEditCheckSettingElements'
import useMultiEditMemberGuids from './components/MultiEditCheckSettings/hooks/useMultiEditMemberGuids'
import MultiEditCheckSettingsTabs from './components/MultiEditCheckSettingsTabs'
import { getAvailableCheckSettingsTabs } from './components/MultiEditCheckSettingsTabs/tabs'
import MultiEditCrossSection from './components/MultiEditCrossSection'
import MultiEditExport from './components/MultiEditExport'
import MultiEditOpenings from './components/MultiEditOpenings'
import MultiEditVerticalConnectionsReset from './components/MultiEditVerticalConnectionsReset'
import WallPlacement from './components/WallPlacement'

interface Props {
  resetElements: () => void
  selectedElements: string[]
}

const MultiEdit = ({ resetElements, selectedElements }: Props): ReactElement => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const {
    actions: { setMultiSelection },
  } = useStructuralPlanningSelection()

  const hideSelectedElements = useCallback(() => {
    addHiddenElementIds(selectedElements)
    resetElements()
  }, [addHiddenElementIds, resetElements, selectedElements])

  const model = useModelStore(state => state.model)

  const guidToElement = useGuidToElementIncludingRips()

  const openingGuidToOpeningWithWall = useMemo(
    () =>
      model.walls.reduce((acc, wall) => {
        ;(wall.openings || []).forEach((opening: Opening) => {
          acc[opening.guid] = { opening, wall, indexInWall: 0 }
        })
        return acc
      }, {} as Record<string, { opening: Opening; wall: ShapeObject; indexInWall: number }>),
    [model.walls],
  )

  const guidToElementIncludingOpenings = useMemo(() => {
    return {
      ...guidToElement,
      ...openingGuidToOpeningWithWall,
    }
  }, [guidToElement, openingGuidToOpeningWithWall])

  const elementTypes = useElementTypes()

  const removeWall = useModelStore(state => state.removeWall)

  const invalidateResultsQueries = useResultsInvalidation()
  const invalidateModel = useModelInvalidation()

  const elements = useMemo(
    () => selectedElements.map(guid => guidToElementIncludingOpenings[guid]),
    [selectedElements, guidToElementIncludingOpenings],
  )

  const multiEditConfigSelectedElements = useMemo(
    () => (selectedElements.length === 0 ? null : multiEditConfig(elements)),
    [elements, selectedElements],
  )

  // Member guids of the current selection that carry check settings, resolved once
  // for every check settings section below.
  const checkSettingsMemberGuids = useMultiEditMemberGuids(
    selectedElements,
    multiEditConfigSelectedElements,
  )
  const checkSettingsElements = useMultiEditCheckSettingElements(checkSettingsMemberGuids)

  // Members without check settings are dropped by the resolver, which also
  // requires computed results — memberCheckSettings is only populated
  // post-calculation, so an uncalculated selection yields no tabs at all.
  const checkSettingsTabs = useMemo(
    () =>
      getAvailableCheckSettingsTabs({
        elements: checkSettingsElements,
        memberGuids: checkSettingsMemberGuids,
      }),
    [checkSettingsElements, checkSettingsMemberGuids],
  )

  // Every check settings section requires the whole selection to resolve, so a
  // single unresolved member (not yet calculated, or without check settings)
  // silences the entire "Nachweiseinstellungen" area — explain why instead of
  // just not rendering it.
  const hasUnresolvedCheckSettingsMembers =
    checkSettingsMemberGuids.length > 0 &&
    checkSettingsElements.length !== checkSettingsMemberGuids.length

  const checkSettingsSection = useCheckSettingsSection()

  const selectedElementsMode = useMemo(() => {
    if (multiEditConfigSelectedElements?.onlyOpenings) return 'Wandöffnungen'
    if (multiEditConfigSelectedElements?.onlyInnerWalls) return 'Innenwände'
    if (multiEditConfigSelectedElements?.onlyOuterWalls) return 'Außenwände'
    if (multiEditConfigSelectedElements?.onlyVerticalRoofSlabs) return 'Dächer'
    if (multiEditConfigSelectedElements?.onlyVerticalSlabs) return 'Decken'
    if (multiEditConfigSelectedElements?.onlyRips) return 'Rippen'
    if (multiEditConfigSelectedElements?.onlyLintels) return 'Stürze'
    if (multiEditConfigSelectedElements?.onlySlabAndRoofBeams) return 'Decken- und Dachbalken'
    if (multiEditConfigSelectedElements?.onlyLineLike) return 'Line-Like (Unterzüge, Rippen etc.)'

    return 'Keine Einschränkung'
  }, [
    multiEditConfigSelectedElements?.onlyOpenings,
    multiEditConfigSelectedElements?.onlyInnerWalls,
    multiEditConfigSelectedElements?.onlyLineLike,
    multiEditConfigSelectedElements?.onlyLintels,
    multiEditConfigSelectedElements?.onlyOuterWalls,
    multiEditConfigSelectedElements?.onlyRips,
    multiEditConfigSelectedElements?.onlyVerticalRoofSlabs,
    multiEditConfigSelectedElements?.onlyVerticalSlabs,
    multiEditConfigSelectedElements?.onlySlabAndRoofBeams,
  ])

  const combinedProperties = useMemo(() => {
    const elements = selectedElements.map(guid => guidToElementIncludingOpenings[guid]) as (
      | ShapeObject
      | ShapeObjectLine
      | PositionForVertical
      | { opening: Opening; wall: ShapeObject; indexInWall: number }
      | undefined
    )[]

    if (selectedElements.length === 0 || !multiEditConfigSelectedElements) return null

    const {
      onlyOpenings,
      onlyInnerWalls,
      onlyOuterWalls,
      onlyVerticalSlabs,
      onlyVerticalRoofSlabs,
      onlyLineLike,
      onlyWalls,
    } = multiEditConfigSelectedElements

    if (onlyOpenings) {
      return {
        exportConfig: { show: false },
      }
    }

    const allInSameStorey = every(
      elements,
      el =>
        el &&
        elements[0] &&
        'storey' in el &&
        'storey' in elements[0] &&
        el.storey === elements[0].storey,
    )

    const firstElement = elements[0]

    let assemblyConfig
    let wallRipsEdgesConfig
    let wallPlacementConfig

    const elementType =
      firstElement && 'element_type' in firstElement && firstElement.element_type
        ? getElementAssemblyType(firstElement.element_type)
        : undefined

    if (elementType) {
      assemblyConfig = {
        show: onlyInnerWalls || onlyOuterWalls || onlyVerticalSlabs || onlyVerticalRoofSlabs,
        elementType,
      }

      wallPlacementConfig = {
        show: onlyWalls,
      }

      if (onlyWalls) {
        let wallLength: number | undefined
        if (elements[0] && 'shape' in elements[0] && 'points' in elements[0].shape) {
          const firstWallLength = elements[0].shape.points[0].distanceTo(
            elements[0].shape.points[1],
          )

          const isEveryWallSameLength = every(elements, el => {
            if (el && 'shape' in el && 'points' in el.shape) {
              return el.shape.points[0].distanceTo(el.shape.points[1]) === firstWallLength
            }

            return false
          })

          if (isEveryWallSameLength) {
            wallLength = firstWallLength
          }
        }

        wallRipsEdgesConfig = {
          show: onlyWalls,
          wallLength,
        }
      }
    }

    let crossSectionConfig

    if (
      firstElement &&
      !('opening' in firstElement) &&
      ('position_guid' in firstElement || 'start' in firstElement.shape)
    ) {
      const guid = 'position_guid' in firstElement ? firstElement.position_guid : firstElement.guid
      const type = elementTypes[guid]
      crossSectionConfig = {
        show: onlyLineLike,
        referenceElement: {
          guid,
          type,
        },
      }
    }

    let deleteConfig

    if (
      onlyInnerWalls ||
      onlyOuterWalls ||
      onlyVerticalSlabs ||
      onlyVerticalRoofSlabs ||
      onlyLineLike ||
      onlyWalls
    ) {
      deleteConfig = {
        show: true,
      }
    }

    const elementTypesForCopyingToOtherStoreys: ElementTypes[] = [
      'vertical_slabs',
      'beams',
      'columns',
      'inner_walls',
      'outer_walls',
    ]

    const showCopyToOtherStoreys =
      allInSameStorey &&
      every(
        elements,
        el =>
          el &&
          'element_type' in el &&
          elementTypesForCopyingToOtherStoreys.includes(el.element_type as ElementTypes),
      )

    return {
      assemblyConfig,
      wallPlacementConfig,
      crossSectionConfig,
      wallRipsEdgesConfig,
      deleteConfig,
      copyToOtherStoreysConfig: {
        show: showCopyToOtherStoreys,
        storey: firstElement && 'storey' in firstElement ? firstElement.storey : undefined,
      },
      exportConfig: { show: onlyLineLike },
    }
  }, [
    elementTypes,
    guidToElementIncludingOpenings,
    multiEditConfigSelectedElements,
    selectedElements,
  ])

  const { mutate: alignSelectedWallsMutation, isLoading: isAligningSelectedWalls } = useMutation(
    () => alignSelectedWalls.request(projectId as string, selectedElements),
    {
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        invalidateResultsQueries(projectId as string)
        enqueueSnackbar('Wände erfolgreich ausgerichtet', { variant: 'success' })
        resetElements?.()
      },
      onError: (error: AxiosError) => {
        invalidateModel(projectId as string)
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ausrichten der Wände'), {
          variant: 'error',
        })
        resetElements?.()
      },
    },
  )

  // Handlers for merge wall form
  const handleCreateWall = async (
    wallData: {
      p1: { x: number; y: number }
      p2: { x: number; y: number }
      storey_idx: number
      placement: WallPlacement
      openings?: Opening[]
    } & WallSettings,
  ) => {
    const newWall = await createWall.request(projectId as string, {
      guid: v4(),
      p1: wallData.p1,
      p2: wallData.p2,
      storey_idx: wallData.storey_idx,
      placement: wallData.placement as 'Internal',
      positionPlanSettings: wallData.positionPlanSettings,
      certificateSettings: wallData.certificateSettings,
    })
    await Promise.all([
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
      queryClient.invalidateQueries(getTensileTransmissionGraph.getKey(projectId)),
    ])
    invalidateResultsQueries(projectId as string)
    return newWall
  }

  const handleDeleteWall = async (wallGuid: string) => {
    await deleteWall.request(projectId as string, wallGuid)
    removeWall(wallGuid)
    await Promise.all([
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
      queryClient.invalidateQueries(getModel.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningProposal.getKey(projectId)),
      queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId)),
      queryClient.invalidateQueries(getTensileTransmissionGraph.getKey(projectId)),
    ])
    invalidateResultsQueries(projectId as string)
  }

  const selectedRips = useMemo(
    () =>
      selectedElements
        .map(guid => find(model.rips, { position_guid: guid }))
        .filter((rip): rip is Rip => !!rip),
    [model.rips, selectedElements],
  )
  // Check if exactly 2 walls are selected for merge
  const canMergeWalls = useMemo(() => {
    if (selectedElements.length !== 2) return false

    const element1 = guidToElement[selectedElements[0]]
    const element2 = guidToElement[selectedElements[1]]

    if (!element1 || !element2) return false

    const isWall1 =
      element1.element_type === 'inner_walls' || element1.element_type === 'outer_walls'
    const isWall2 =
      element2.element_type === 'inner_walls' || element2.element_type === 'outer_walls'
    return isWall1 && isWall2
  }, [selectedElements, guidToElement])

  return (
    <Stack direction="column" spacing={1} p={1} data-cy="multi-edit-panel">
      <Stack p={1} border={1} borderColor="grey.200" borderRadius={1} spacing={1}>
        <Typography mb={1} textAlign="center">
          Wählen Sie weitere Elemente an: {selectedElementsMode}
        </Typography>

        {resetElements && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-evenly">
            <Button
              onClick={resetElements}
              size="small"
              variant="outlined"
              fullWidth
              data-cy="multi-edit-deselect-button"
            >
              abwählen (
              <strong data-cy="multi-edit-selected-elements-text">{selectedElements.length}</strong>
              )
            </Button>
            <HideButton
              onClick={hideSelectedElements}
              size="small"
              fullWidth
              data-cy="btn-multi-edit-hide"
            />
          </Stack>
        )}
      </Stack>

      <Stack direction="column">
        {multiEditConfigSelectedElements?.onlyOpenings && (
          <CollapsibleOpeningsBoxSection
            label="Öffnungen ausrichten"
            defaultExpanded={true}
            dataCy="accordion-multi-edit-openings"
          >
            <MultiEditOpenings
              resetElements={resetElements}
              selectedElements={selectedElements}
              onUseOpeningAsReference={openingGuid =>
                // Re-select the same multi-selection with the reference opening first.
                setMultiSelection([
                  openingGuid,
                  ...selectedElements.filter(guid => guid !== openingGuid),
                ])
              }
            />
          </CollapsibleOpeningsBoxSection>
        )}

        {combinedProperties?.assemblyConfig?.show && (
          <CollapsibleAssemblyBoxSection
            defaultExpanded={true}
            dataCy="accordion-assembly-assignment"
          >
            <Stack direction="column" spacing={1} width="100%">
              <AssemblyAssignmentForm
                elementType={combinedProperties?.assemblyConfig.elementType}
                selectedElements={selectedElements}
                showSelectedElementsNumber={false}
                autoFocus={false}
                showBorder={false}
              />
              <ElementAssembliesTable elementGuids={selectedElements} />
            </Stack>
          </CollapsibleAssemblyBoxSection>
        )}

        {combinedProperties?.wallPlacementConfig?.show && (
          <FormCollapsibleSection
            label="Platzierung"
            startIcon={FormSectionIcons.geometry}
            defaultExpanded={true}
            dataCy="accordion-wall-placement"
          >
            <Stack direction="column" spacing={2} width="100%" pt={1}>
              <WallPlacement elementGuids={selectedElements} />
            </Stack>
          </FormCollapsibleSection>
        )}

        {combinedProperties?.wallRipsEdgesConfig?.show && (
          <CollapsibleWallRipsBoxSection
            label="Rippen auf Wänden"
            defaultExpanded={true}
            dataCy="accordion-wall-rips-edges"
          >
            <Stack direction="column" spacing={2} width="100%" pt={1}>
              <Typography variant="h6">Rippen auf Wänden hinzufügen</Typography>
              <MultiEditWallRipsStartEnd
                elementGuids={selectedElements}
                wallLength={combinedProperties?.wallRipsEdgesConfig?.wallLength}
              />
            </Stack>
          </CollapsibleWallRipsBoxSection>
        )}

        {multiEditConfigSelectedElements?.onlyRips && selectedRips.length > 0 && (
          <CollapsibleCopyBoxSection
            label="Rippen kopieren"
            defaultExpanded={true}
            dataCy="accordion-copy-rips"
          >
            <RipCopyForm
              rips={selectedRips}
              selectionModeKey={COPY_RIPS_MULTI_TARGET_SELECTION_KEY}
            />
          </CollapsibleCopyBoxSection>
        )}

        {combinedProperties?.exportConfig?.show && (
          <FormCollapsibleSection
            label="Export-Status"
            startIcon={FormSectionIcons.documentation}
            defaultExpanded={true}
            dataCy="accordion-multi-edit-export"
          >
            <MultiEditExport elementGuids={selectedElements} />
          </FormCollapsibleSection>
        )}

        {combinedProperties?.crossSectionConfig && (
          <CollapsibleAssemblyBoxSection
            label="Querschnitt"
            defaultExpanded={true}
            dataCy="accordion-cross-section"
          >
            <MultiEditCrossSection
              elementGuids={selectedElements}
              referenceElementGuid={combinedProperties?.crossSectionConfig.referenceElement.guid}
              referenceElementType={
                combinedProperties?.crossSectionConfig.referenceElement.type as ElementTypes
              }
            />
          </CollapsibleAssemblyBoxSection>
        )}

        {checkSettingsTabs.length > 0 && (
          <CollapsibleBox
            variant="expander"
            label="Nachweiseinstellungen"
            startIcon={FormSectionIcons.settings}
            isExpanded={checkSettingsSection.isExpanded}
            handleToggle={() => checkSettingsSection.handleToggleExpanded()}
            dataCy="accordion-multi-edit-check-settings"
          >
            <MultiEditCheckSettingsTabs tabs={checkSettingsTabs} elements={checkSettingsElements} />
          </CollapsibleBox>
        )}

        {checkSettingsTabs.length === 0 && hasUnresolvedCheckSettingsMembers && (
          <ErrorFieldBase
            severity="warning"
            message="Nachweiseinstellungen sind für die aktuelle Auswahl nicht verfügbar — nicht alle ausgewählten Elemente unterstützen sie oder ihre Ergebnisse liegen noch nicht vor."
            data-cy="multi-edit-check-settings-unavailable-warning"
          />
        )}

        {combinedProperties?.copyToOtherStoreysConfig?.show && (
          <CollapsibleCopyBoxSection label="Kopieren" defaultExpanded={true}>
            <CopyToOtherStoreys
              selectedElements={selectedElements}
              storey={combinedProperties?.copyToOtherStoreysConfig?.storey}
            />
          </CollapsibleCopyBoxSection>
        )}

        <CollapsibleActionsBoxSection
          label="Aktionen"
          defaultExpanded={true}
          dataCy="accordion-multi-select-actions"
        >
          <Stack direction="column" spacing={1} width="100%">
            <Stack direction="row" spacing={1} width="100%" alignItems="center">
              {multiEditConfigSelectedElements?.onlyRips && (
                <MultiEditVerticalConnectionsReset elementGuids={selectedElements} />
              )}
              {combinedProperties?.wallPlacementConfig?.show && (
                <WallAlignmentConfirmDialog onConfirm={() => alignSelectedWallsMutation()}>
                  <AlignmentButton
                    loading={isAligningSelectedWalls}
                    data-cy="align-selected-walls-btn"
                    sx={{ paddingX: 1, paddingY: 0.5 }}
                  >
                    an SW zuschneiden
                  </AlignmentButton>
                </WallAlignmentConfirmDialog>
              )}
              {canMergeWalls && (
                <MergeWallForm
                  wall1_guid={selectedElements[0]}
                  wall2_guid={selectedElements[1]}
                  handleCreate={handleCreateWall}
                  handleDelete={handleDeleteWall}
                  onSuccess={resetElements}
                />
              )}
              {combinedProperties?.deleteConfig?.show && (
                <MultiDelete selectedElements={selectedElements} resetSelected={resetElements} />
              )}
            </Stack>
          </Stack>
        </CollapsibleActionsBoxSection>
      </Stack>
    </Stack>
  )
}

export default MultiEdit
